/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ATTENTIVE_COOKIE_PREFIXES } from '@/components/attentive/attentive.utils';
import { readCookie } from '@/lib/cookie-utils.client';

/**
 * OneTrust's consent group ids. These are OneTrust's own fixed identifiers, shared with the SFRA
 * sites — the human-readable names come from the Black Magic OneTrust domain configuration.
 */
export const OneTrustCategory = {
    /** Always on; cannot be declined. */
    StrictlyNecessary: 'C0001',
    /** "Performance" — analytics and measurement. Adobe Analytics lives here. */
    Performance: 'C0002',
    /** "Functional" — enhanced functionality and personalisation. Drives the Salesforce DNT signal. */
    Functional: 'C0003',
    /** "Targeted Advertising & Social Media" — Attentive's tracking lives here. */
    Targeting: 'C0004',
} as const;

export type OneTrustCategoryId = (typeof OneTrustCategory)[keyof typeof OneTrustCategory];

/**
 * Cookie name prefixes to remove when a category is declined, keyed by the category that permits
 * them. Prefixes, not exact names: vendors shard and version cookie names (`__attentive_pv`,
 * `AMCV_<orgid>`), so an exact list silently goes stale and leaves cookies behind.
 *
 * `C0001` has no entry by design — strictly necessary cookies are never removed. `C0003` has none
 * either: the functional signal is carried by `dw_dnt` server-side rather than by browser cookies
 * this code can see.
 *
 * This is a best-effort sweep for cookies already on the device, not a blocker. A tag that is still
 * loaded will re-set its cookies on the next interaction — see the note on `clearAttentiveCookies`.
 */
export const CATEGORY_COOKIE_PREFIXES: Partial<Record<OneTrustCategoryId, readonly string[]>> = {
    // Adobe Analytics (AppMeasurement, loaded by the Launch property), plus `AMCV_`/`AMCVS_` for
    // the Experience Cloud id service. Deleting these is the visible half of declining C0002; the
    // load gate in applyConsent is the half that actually stops the beacons.
    //
    // `s_` rather than an enumerated list (`s_cc`, `s_sq`, `s_vi`, `s_fid`, …) because that list
    // was already wrong: it missed `s_pv`, which survived every sweep. AppMeasurement's plugins
    // each add their own `s_*` cookie (`s_ppv`, `s_tp`, `s_nr`, `s_ips`, `s_depth`), so an explicit
    // list silently rots as the Launch property gains extensions. No first-party cookie on this
    // storefront starts with `s_` — the near misses are `site_id`, `selectedStoreInfo` and
    // `storefront-next-context`, none of which have the underscore — so the broad prefix is safe.
    // Check that still holds before adding a cookie named `s_*` in app code.
    [OneTrustCategory.Performance]: ['s_', 'AMCV_', 'AMCVS_'],
    [OneTrustCategory.Targeting]: ATTENTIVE_COOKIE_PREFIXES,
};

/** The subset of the OneTrust SDK runtime API this integration calls. */
export interface OneTrustApi {
    /** Opens (or closes) the preference centre. Bound by the SDK once the banner script has loaded. */
    ToggleInfoDisplay?: () => void;
    /** Registers a callback fired whenever the shopper changes their consent. */
    OnConsentChanged?: (callback: () => void) => void;
}

declare global {
    interface Window {
        /** OneTrust SDK runtime API, installed by `otSDKStub.js`. */
        OneTrust?: OneTrustApi;
        /**
         * Comma-delimited list of the shopper's granted groups, e.g. `,C0001,C0003,`. Set by the
         * SDK before it invokes {@link Window.OptanonWrapper}. Note the spelling — OneTrust also
         * defines `OptanonActiveGroups`, but `OnetrustActiveGroups` is the one kept current.
         */
        OnetrustActiveGroups?: string;
        /** Callback the OneTrust SDK invokes on load and after every consent change. */
        OptanonWrapper?: () => void;
        /** Set by the consent bridge; receives the {@link Window.OptanonWrapper} callbacks. */
        __otHandler?: () => void;
        /**
         * Callbacks that arrived before the bridge hydrated. The `<head>` shim pushes to this and
         * the bridge drains it on mount — the same queue-then-replay shape as `adobeDataLayer`.
         */
        __otQueue?: unknown[];
    }
}

/**
 * The groups the shopper has granted.
 *
 * Prefers the `OnetrustActiveGroups` global, falling back to parsing the `OptanonConsent` cookie.
 * The fallback matters on a client-side navigation in a slow-network session: the bridge can run
 * before the banner SDK has installed its globals, and the cookie is already there from a previous
 * visit. Returns an empty list when consent is genuinely undetermined, so callers fail closed.
 */
export function getActiveGroups(): OneTrustCategoryId[] {
    if (typeof window === 'undefined') {
        return [];
    }

    const fromGlobal = window.OnetrustActiveGroups;
    if (typeof fromGlobal === 'string' && fromGlobal.trim()) {
        return parseActiveGroupsList(fromGlobal);
    }

    return parseConsentCookie(readCookie('OptanonConsent'));
}

/** Split OneTrust's `,C0001,C0003,` format into ids, discarding the empty leading/trailing fields. */
function parseActiveGroupsList(value: string): OneTrustCategoryId[] {
    return value
        .split(',')
        .map((group) => group.trim())
        .filter(Boolean) as OneTrustCategoryId[];
}

/**
 * Pull the granted groups out of the `OptanonConsent` cookie.
 *
 * The cookie is a URL-encoded query string whose `groups` field pairs each id with 0 or 1, e.g.
 * `groups=C0001%3A1%2CC0002%3A0`. Only the `:1` entries count as granted.
 *
 * `URLSearchParams` percent-decodes each key and value itself, so the raw cookie value goes in
 * undecoded. Pre-decoding with `decodeURIComponent` is not just redundant, it throws `URIError` on
 * a malformed sequence — and an uncaught throw here would propagate through `getActiveGroups()`
 * into `applyConsent()` and take down every category, on the code path whose whole job is to be
 * the robust fallback. `URLSearchParams` tolerates the same input without throwing.
 */
function parseConsentCookie(cookieValue: string | undefined): OneTrustCategoryId[] {
    if (!cookieValue) {
        return [];
    }
    const groups = new URLSearchParams(cookieValue).get('groups');
    if (!groups) {
        return [];
    }
    return groups
        .split(',')
        .map((pair) => {
            const [id, granted] = pair.split(':');
            return granted?.trim() === '1' ? id?.trim() : undefined;
        })
        .filter((id): id is OneTrustCategoryId => Boolean(id));
}

/** Whether the shopper has granted a given category. */
export function hasCategory(category: OneTrustCategoryId, activeGroups = getActiveGroups()): boolean {
    return activeGroups.includes(category);
}

/**
 * Whether the shopper has made a choice at all. OneTrust sets `OptanonAlertBoxClosed` to a
 * timestamp when the banner is dismissed, so its absence means "banner still showing, or the
 * visitor's jurisdiction does not require one".
 */
export function hasInteractedWithBanner(): boolean {
    return Boolean(readCookie('OptanonAlertBoxClosed'));
}

/**
 * Open the OneTrust preference centre.
 *
 * OneTrust also auto-binds any element carrying the `ot-sdk-show-settings` class (this is how the
 * SFRA sites do it), but that binding is installed once, at SDK load. In a single-page app a link
 * rendered by a later navigation would never be wired up, so call the API explicitly instead.
 */
export function openPreferenceCenter(): void {
    window.OneTrust?.ToggleInfoDisplay?.();
}
